import { datadogLogs } from '@datadog/browser-logs';
import { conditionalString } from '@strata/helper-functions';
import { ErrorDialog } from '@strata/react-dialog';
import { ErrorToast } from '@strata/react-toast-message';
import { useState } from 'react';

export const formatErrorDetails = (isError, errorDetails) => {
    if (isError) {
        return (
            <ul>
                {errorDetails ? (
                    // eslint-disable-next-line react/no-array-index-key
                    errorDetails.map((err, i) => <li key={i}>{err}</li>)
                ) : (
                    <li>Unknown Error</li>
                )}
            </ul>
        );
    }

    return <p />;
};

export const parseValidationError = (lastError) => {
    const isError = lastError != null;
    if (isError) {
        try {
            if (lastError?.message) {
                const errorObject = JSON.parse(lastError.message);
                return [
                    isError,
                    errorObject.detail.message ?? 'Request Unsuccessful',
                    errorObject.detail.details ??
                        formatErrorDetails(true, [
                            `Status Code: ${lastError.status}`,
                            ...(errorObject?.detail.map((e) => JSON.stringify(e)) ?? []),
                        ]),
                ];
            }
            return [isError, lastError.errorTitle, lastError.errorDetails];
        } catch {
            datadogLogs.logger.error(lastError);
            return [isError, 'Unknown Error Return', ['The API returned an unexpected error.']];
        }
    }

    return [isError, '', []];
};

export const handleError = (message, error) => {
    // Parse and format the error string
    let parsedError;
    try {
        const errorDetail = JSON.parse(error?.message)?.detail;
        parsedError = typeof errorDetail === 'object' ? errorDetail : { message: errorDetail };
    } catch {
        parsedError = { message: error?.message };
    } finally {
        const errorMessage = parsedError?.message ?? message;
        let errorMessageDetails = error?.data?.detail || parsedError?.details;
        errorMessageDetails = conditionalString(!!errorMessageDetails, ` ${errorMessageDetails}`);
        const displayMessage = `${errorMessage}${errorMessageDetails}`;

        ErrorToast({ message: displayMessage });
        datadogLogs.logger.error(displayMessage, { error });
        // eslint-disable-next-line no-console
        console.error(displayMessage, error);
    }
};

export const useLastErrorDialog = () => {
    const [lastError, setLastError] = useState(undefined);

    const parseAndSetLastError = (error) => {
        const [isError, errorTitle, errorDetails] = parseValidationError(error);
        setLastError({
            isError,
            errorTitle,
            errorDetails,
        });
    };

    const ErrorDialogComponent = () => (
        <ErrorDialog
            dialogTitle={lastError?.errorTitle ?? ''}
            isOpen={lastError?.isError}
            onSecondaryButtonClick={() => setLastError(undefined)}
            dialogContent={lastError?.errorDetails}
        />
    );

    return { lastError, setLastError, parseAndSetLastError, ErrorDialogComponent };
};
